import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, KeyRound, Lock, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { adminApi, clearSession, isAdmin, rememberSession } from "@/lib/auth";

const BG_URL =
  "https://images.unsplash.com/photo-1554684726-0830be8cdbcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdoaXRlJTIwYXJjaGl0ZWN0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3Nzg1MzU0OTR8MA&ixlib=rb-4.1.0&q=85";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PhoneScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  // Si el gestor ya tiene sesión guardada, entra directo a su panel.
  // Si viene del panel pulsando «Inicio», no se le devuelve automáticamente.
  const [checkingSession, setCheckingSession] = useState(
    () => !location.state?.fromAdmin && isAdmin() && rememberSession()
  );
  const logoShown = useRef(null);
  if (!logoShown.current) {
    let resolve;
    const promise = new Promise((r) => { resolve = r; });
    logoShown.current = { promise, resolve };
  }

  useEffect(() => {
    if (!checkingSession) return;
    let cancelled = false;
    // El logo se ve al menos 2 segundos: se cuentan desde que aparece (si la
    // imagen tarda más de 3 s en llegar, se empieza a contar igualmente).
    const fallback = setTimeout(logoShown.current.resolve, 3000);
    const minSplash = logoShown.current.promise.then(
      () => new Promise((resolve) => setTimeout(resolve, 2000))
    );
    Promise.all([adminApi.get("/auth/me-admin"), minSplash])
      .then(() => {
        if (!cancelled) navigate("/admin", { replace: true });
      })
      .catch(async () => {
        await minSplash;
        if (cancelled) return;
        clearSession();
        setCheckingSession(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(fallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) {
      toast.error("Introduce el código del curso de 4 dígitos.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/access/pin`, { pin });
      if (!data?.course_id) {
        throw new Error("Curso no encontrado.");
      }
      sessionStorage.setItem("course_pin", pin);
      sessionStorage.setItem("course_id", data.course_id);
      sessionStorage.setItem("course_name", data.course_name || "");
      sessionStorage.setItem("region_name", data.region_name || "");
      sessionStorage.setItem("course_type", data.course_type || "");
      // Datos del curso que se imprimen en la ficha RGPD que firma el alumno.
      sessionStorage.setItem("course_info", JSON.stringify(data));
      sessionStorage.setItem(
        "session2_open", JSON.stringify(!!data.session2_open)
      );
      sessionStorage.setItem(
        "sign_session", data.sign_session ? String(data.sign_session) : ""
      );
      sessionStorage.setItem(
        "exam_available", JSON.stringify(!!data.exam_available)
      );
      navigate("/student");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || "PIN inválido. Pídele el código al instructor.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div
        className="relative min-h-screen w-full flex items-center justify-center px-6"
        data-testid="session-restore-loader"
      >
        <div className="flex flex-col items-center gap-4">
          <img
            src="/desfiemer_logo.png"
            alt="Desfiemer"
            className="h-20 w-auto max-w-[90vw] object-contain select-none"
            data-testid="session-restore-logo"
            onLoad={() => logoShown.current.resolve()}
            onError={() => logoShown.current.resolve()}
            draggable={false}
          />
          <p className="text-sm font-medium text-zinc-500">
            Abriendo tu panel de gestión…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6 py-12"
      data-testid="phone-screen"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-white/85 backdrop-blur-xl" aria-hidden />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-6 md:mb-8">
          <img
            src="/desfiemer_logo.png"
            alt="Desfiemer"
            className="h-10 md:h-12 w-auto select-none"
            data-testid="desfiemer-logo"
            draggable={false}
          />
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-24px_rgba(0,0,0,0.18)] p-8 md:p-10">
          <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
            ALUMNO
          </h1>
          <p className="mt-3 text-zinc-600 text-base">
            Introduce el <strong>código del curso</strong> que te ha dado el
            instructor.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            <div>
              <label
                htmlFor="pin"
                className="block text-sm font-medium text-zinc-700 mb-2"
              >
                Código del curso (4 dígitos)
              </label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  id="pin"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0000"
                  value={pin}
                  onChange={(e) =>
                    setPin(e.target.value.replace(/[^\d]/g, "").slice(0, 4))
                  }
                  className="touch-input pl-12 text-2xl tracking-[0.5em] text-center font-mono border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="course-pin-input"
                  required
                  autoFocus
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || pin.length !== 4}
              className="w-full h-14 text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md transition-colors disabled:opacity-60"
              data-testid="pin-submit-button"
            >
              <span>{loading ? "Comprobando…" : "Entrar"}</span>
              {!loading && <ArrowRight className="ml-2 h-5 w-5" />}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/login")}
              className="inline-flex items-center justify-center gap-1.5 h-11 px-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 border border-zinc-300 rounded-md transition-colors"
              data-testid="go-to-admin-login"
            >
              <Lock className="h-3.5 w-3.5" />
              GESTOR
            </button>
            <button
              type="button"
              onClick={() => navigate("/instructor/login")}
              className="inline-flex items-center justify-center gap-1.5 h-11 px-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 border border-zinc-300 rounded-md transition-colors"
              data-testid="go-to-instructor-login"
            >
              <GraduationCap className="h-3.5 w-3.5" />
              INSTRUCTOR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
